/**
 * Playing a catalogue track — the studio's audition, and the book's bed.
 *
 * One contract, three jobs:
 *
 *  · **Audition.** A creator taps a row in the music sheet and hears it. That is
 *    a *preview*: it loops, it is never in a published book, and starting another
 *    one always replaces it rather than layering on top.
 *  · **Bed.** A chosen track loops under a whole book or capsule. This is the one
 *    case where the volume is not the player's business alone — it is ducked under
 *    any voice (see `#/lib/musicDuck`) — so a bed is handed back as a *voice* with
 *    a `setLevel` the caller drives.
 *  · **Silence.** A creator may choose no music at all, and then nothing is
 *    returned and nothing plays. Silence is a selection, not a failure.
 *
 * The two platforms are played by their own means rather than one call that
 * pretends to be both. On the web that means an `<audio>` element, which is what
 * the reading-audio engine already uses and the only thing with a reliable volume
 * to fade: a bed has to be *ramped* down under a voice, and a web player that
 * reports success but exposes no volume is a duck that does not happen. On a
 * device it means `expo-audio`, whose looping player is the platform's own.
 *
 * What the two share is everything a caller can see: the same voice type, the
 * same fade, and the same rule that a track plays at the app's own level and
 * never louder.
 */

import {Platform} from 'react-native'
/*
 * `expo-asset` ships with the Expo SDK rather than as a direct dependency of this
 * app, and it is the only thing that turns a `require()`d module into a URI on
 * both platforms. The same suppression `#/lib/sounds` carries, for the same two
 * packages and the same reason.
 */
/* eslint-disable import-x/no-extraneous-dependencies */
import {Asset} from 'expo-asset'
import {type AudioPlayer} from 'expo-audio'

/* eslint-enable import-x/no-extraneous-dependencies */
import {type MusicSource, type MusicTrack} from '#/lib/backgroundMusic'
import {fadeLevels, MUSIC_FADE_MS, MUSIC_FADE_STEP_MS} from '#/lib/musicDuck'
import {getMasterVolume, getMuted} from '#/lib/sounds'

/**
 * A looping piece of music, under whatever is driving it.
 *
 * `setLevel` is what ducking *is*, and nothing outside this module should reach
 * the underlying player — a surface that paused the bed directly would leave the
 * player's own state and the app's disagreeing. The other five are the
 * *audition*'s controls: a creator can hold a preview where it is, continue it,
 * and be shown how far in it has got — and both platforms can answer all three.
 */
export type MusicVoice = {
  /** Fade out and release. Safe to call twice. */
  stop(): void
  /** Ramp to a level, 0–1, of the app's own output. */
  setLevel(level: number): void
  /** Hold the voice where it is. Ignored once it is released. */
  pause(): void
  /** Continue from where it was held. Ignored once it is released. */
  resume(): void
  /** Whether the voice is held rather than playing. */
  isPaused(): boolean
  /** How far into the track it is, in ms — `null` when unreadable. */
  positionMs(): number | null
  /** The whole track's length, in ms — `null` while it is unknown. */
  durationMs(): number | null
}

/** How loud music sits relative to the app's volume. Ambience, not a feature. */
const BED_SHARE = 0.5
/** A track being auditioned is the thing the creator is listening to. */
const PREVIEW_SHARE = 0.75
/** Shortest fade a release gets, so a stop never hangs on a long ramp. */
const RELEASE_FADE_MS = 200

/** The app's own level, so music can never be louder than the app's setting. */
export function musicLevel(share: number = BED_SHARE): number {
  return (getMuted() ? 0 : getMasterVolume()) * share
}

/** A platform clock in seconds, as milliseconds — or `null` when it is not one. */
function secondsMs(seconds: number): number | null {
  return Number.isFinite(seconds) && seconds >= 0
    ? Math.round(seconds * 1000)
    : null
}

/**
 * Where a track's audio actually is.
 *
 * A track goes through `expo-asset`, which is what turns a `require()`d module
 * into a URI on both platforms. Resolved here rather than by a player, because it
 * is the one step both platforms need.
 */
async function resolveMusicUri(source: MusicSource): Promise<string | null> {
  try {
    const [asset] = await Asset.loadAsync(source.module)
    return asset?.uri ?? null
  } catch {
    return null
  }
}

/** How the audition's controls reach the platform the voice plays on. */
type VoiceControls = {
  pause: () => void
  resume: () => void
  isPaused: () => boolean
  positionMs: () => number | null
  durationMs: () => number | null
}

/**
 * A voice that ramps its own volume.
 *
 * `set` and `release` are the platform, and everything above them — starting
 * silent, ramping up, ramping down before letting go, remembering the level its
 * owner asked for so an unduck returns *there* and not to a constant — is the
 * same on both. Ducking multiplies a target rather than replacing it, which is
 * why the target is held here and not recomputed from a guess.
 */
function rampedVoice(
  set: (volume: number) => void,
  release: () => void,
  level: number,
  fadeMs: number = MUSIC_FADE_MS,
  controls: VoiceControls,
): MusicVoice {
  let current = 0
  let target = level
  let timer: ReturnType<typeof setInterval> | null = null
  let stopped = false

  const apply = (value: number) => {
    try {
      set(value)
    } catch {
      // A voice whose platform has already gone away is not worth raising over.
    }
  }

  const clear = () => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  const rampTo = (to: number) => {
    if (stopped) return
    clear()
    const steps = fadeLevels(current, to, fadeMs, MUSIC_FADE_STEP_MS)
    let i = 0
    timer = setInterval(() => {
      const next = steps[i++]
      if (next === undefined) {
        clear()
        current = to
        return
      }
      current = next
      apply(current)
    }, MUSIC_FADE_STEP_MS)
  }

  // Start silent and fade up, so a track never begins with a click.
  rampTo(target)

  return {
    stop() {
      if (stopped) return
      clear()
      stopped = true
      const out = fadeLevels(
        current,
        0,
        Math.min(fadeMs, RELEASE_FADE_MS),
        MUSIC_FADE_STEP_MS,
      )
      let i = 0
      const releaseTimer = setInterval(() => {
        const next = out[i++]
        if (next === undefined) {
          clearInterval(releaseTimer)
          release()
          return
        }
        apply(next)
      }, MUSIC_FADE_STEP_MS)
    },
    setLevel(next) {
      if (stopped) return
      target = next
      rampTo(next)
    },
    /*
     * The audition's controls, refused once the voice is released: a surface
     * holding a released voice could otherwise continue a track that is already
     * fading out.
     */
    pause() {
      if (!stopped) controls.pause()
    },
    resume() {
      if (!stopped) controls.resume()
    },
    isPaused() {
      return stopped ? true : controls.isPaused()
    },
    positionMs() {
      return stopped ? null : controls.positionMs()
    },
    durationMs() {
      return stopped ? null : controls.durationMs()
    },
  }
}

/**
 * Put the bed's element in the document, hidden.
 *
 * Two reasons, and the reading-audio player (`#/lib/flashbookAudio`) learned the
 * first one the hard way: **WebKit refuses to play a media element that is not
 * attached to a page** — `play()` resolves nothing, `currentTime` never moves, no
 * error is raised — so a bed created off to one side is a bed that may simply
 * never be heard on Safari, and it fails looking exactly like music nobody chose.
 *
 * The second is that a bed that is in the document is a bed whose *level* the app
 * can see: the ramp lives in an element that is otherwise invisible to everything
 * above it, which is what a gate about a duck needs to be able to read.
 *
 * Hidden by size and opacity rather than `display: none`, for the reason above —
 * some engines treat a non-rendered element as "not rendered" and refuse it.
 */
function attachBedElement(element: HTMLAudioElement): void {
  if (typeof document === 'undefined' || !document.body) return
  element.setAttribute('playsinline', 'true')
  element.style.position = 'fixed'
  element.style.left = '0'
  element.style.bottom = '0'
  element.style.width = '1px'
  element.style.height = '1px'
  element.style.opacity = '0'
  element.style.pointerEvents = 'none'
  document.body.appendChild(element)
}

/** Play a URI on the web, where an audio element is the whole of the story. */
async function playOnWeb(
  uri: string,
  level: number,
  fadeMs: number,
): Promise<MusicVoice | null> {
  const element = new Audio(uri)
  element.loop = true
  element.volume = 0
  attachBedElement(element)
  try {
    await element.play()
  } catch {
    // Autoplay refused, or the file is not playable. Either way there is no
    // music, and saying so is better than reporting a bed that is silent.
    element.remove()
    return null
  }
  return rampedVoice(
    volume => {
      element.volume = volume
    },
    () => {
      try {
        element.pause()
        element.removeAttribute('src')
        element.load()
        element.remove()
      } catch {}
    },
    level,
    fadeMs,
    {
      pause: () => element.pause(),
      resume: () => {
        // A refused resume leaves the element where it was, and the state read
        // is the element's own — so the row keeps saying *held* rather than
        // claiming to play something that is silent.
        void element.play().catch(() => {})
      },
      isPaused: () => element.paused,
      positionMs: () => secondsMs(element.currentTime),
      durationMs: () => secondsMs(element.duration),
    },
  )
}

/** Play a module on a device, where the platform owns the player. */
async function playOnDevice(
  source: MusicSource,
  level: number,
  fadeMs: number,
): Promise<MusicVoice | null> {
  const {createAudioPlayer} = await import('expo-audio')
  let player: AudioPlayer | null = null
  try {
    player = createAudioPlayer(source.module)
    player.loop = true
    player.volume = 0
    // `play()` on a device asks for playback and returns; it is not a promise to
    // await, and a device that cannot play reports it by never starting rather
    // than by rejecting here.
    player.play()
  } catch {
    try {
      player?.remove()
    } catch {}
    return null
  }
  const voice = player
  return rampedVoice(
    volume => {
      voice.volume = volume
    },
    () => {
      try {
        voice.pause()
        voice.remove()
      } catch {}
    },
    level,
    fadeMs,
    {
      pause: () => voice.pause(),
      resume: () => voice.play(),
      isPaused: () => voice.paused,
      positionMs: () => secondsMs(voice.currentTime),
      durationMs: () => secondsMs(voice.duration),
    },
  )
}

/**
 * Start a track, looped, and hand back the voice driving it.
 *
 * `null` means the track could not be played — no source, no URI, or a platform
 * that refused. Callers treat that as "no music", never as an error, because a
 * book whose bed fails to load should still read.
 */
export async function playMusicTrack(
  track: MusicTrack,
  options: {level?: number; fadeMs?: number} = {},
): Promise<MusicVoice | null> {
  const level = options.level ?? musicLevel()
  const fadeMs = options.fadeMs ?? MUSIC_FADE_MS
  if (Platform.OS === 'web') {
    const uri = await resolveMusicUri(track.source)
    if (!uri) return null
    return playOnWeb(uri, level, fadeMs)
  }
  return playOnDevice(track.source, level, fadeMs)
}

/**
 * The licence to audition more than one track in a row.
 *
 * Starting a track is asynchronous, so two quick taps can leave one playing while
 * the other is still resolving — and the loser then starts *underneath* the
 * winner. A monotonic ticket is the cheapest way to make the last tap win.
 */
let _previewTicket = 0
let _preview: {id: string; voice: MusicVoice} | null = null
const _previewListeners = new Set<() => void>()

function announcePreview() {
  for (const listener of _previewListeners) listener()
}

/**
 * What is being auditioned, and where in it the creator is.
 *
 * One reading rather than three functions, because the three are always asked
 * together and a sheet that read them separately could draw a pause button over a
 * track that had already stopped. The position is read *live* — a surface asks
 * for this again to move a progress bar — while the moments that change what the
 * surface is showing are announced through {@link subscribeMusicPreview}.
 */
export type MusicPreviewSnapshot = {
  id: string
  /** Held where it is, rather than playing. */
  paused: boolean
  /** How far in, in ms — `null` while the platform cannot say. */
  positionMs: number | null
  /** The whole track's length, in ms — `null` while the platform cannot say. */
  durationMs: number | null
}

/** The audition a surface is drawing, or `null` when there is none. */
export function musicPreview(): MusicPreviewSnapshot | null {
  if (!_preview) return null
  const {id, voice} = _preview
  return {
    id,
    paused: voice.isPaused(),
    positionMs: voice.positionMs(),
    durationMs: voice.durationMs(),
  }
}

/** Re-render whatever shows the audition when it starts, is held, or stops. */
export function subscribeMusicPreview(listener: () => void): () => void {
  _previewListeners.add(listener)
  return () => {
    _previewListeners.delete(listener)
  }
}

/**
 * Audition a track. Resolves `true` only if *this* audition is the live one.
 *
 * A sheet closes its own audition, but it cannot be the only thing that stops it:
 * leaving a screen is not the same as pressing stop, and music that follows a
 * creator out of the sheet is the bug this module has to be able to prevent on
 * its own. Hence `stopMusicPreview`, which every surface's teardown calls.
 */
export async function previewMusicTrack(track: MusicTrack): Promise<boolean> {
  // The ticket is taken *after* the stop: stopping is itself a new request, so a
  // ticket read before it would be stale before the audio had even loaded, and
  // every audition would cancel itself.
  stopMusicPreview()
  const ticket = ++_previewTicket
  const voice = await playMusicTrack(track, {level: musicLevel(PREVIEW_SHARE)})
  if (!voice) return false
  // A newer tap arrived while this one was loading: this audition is already over.
  if (ticket !== _previewTicket) {
    voice.stop()
    return false
  }
  _preview = {id: track.id, voice}
  announcePreview()
  return true
}

/**
 * Hold the current audition where it is.
 *
 * A no-op when there is none, or when it is already held — the button asks this
 * on every tap while it says *Pause*, and only a change the platform actually
 * made is announced.
 */
export function pauseMusicPreview(): void {
  if (!_preview || _preview.voice.isPaused()) return
  _preview.voice.pause()
  announcePreview()
}

/**
 * Continue the current audition from where it was held.
 *
 * A resume the platform refuses leaves the voice held, and the snapshot keeps
 * reading the platform's own answer — so the button stays on *Resume* rather than
 * claiming to play something that is silent.
 */
export function resumeMusicPreview(): void {
  if (!_preview || !_preview.voice.isPaused()) return
  _preview.voice.resume()
  announcePreview()
}

/** Stop the audition. Does nothing when there is none. */
export function stopMusicPreview(): void {
  _previewTicket++
  if (!_preview) return
  const {voice} = _preview
  _preview = null
  voice.stop()
  announcePreview()
}

/**
 * Follow the app's sound setting.
 *
 * A preview can outlive a change to the master volume — the settings sheet is one
 * tap away — and a preview that keeps playing at the old level is a preview that
 * ignores the mute switch.
 */
export function refreshMusicPreviewLevel(): void {
  if (!_preview) return
  _preview.voice.setLevel(musicLevel(PREVIEW_SHARE))
}
